import type { QueryClient } from '@tanstack/react-query';

import { PORTFOLIO_QUERY_KEYS } from '@/api/portfolio/queries';
import { invalidateTransactionViews } from '@/api/transaction-views/cache-invalidation';
import { getTransactionHashLookupOptions } from '@/api/transaction-views/queries';
import { requestWalletSync, WalletSyncTrigger } from '@/api/wallet/queries';

const RETRY_DELAYS_MS = [0, 1_000, 2_000, 4_000, 8_000] as const;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, milliseconds);
  });

export const reconcileSubmittedTransaction = async (
  queryClient: QueryClient,
  digest: string,
): Promise<void> => {
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await wait(RETRY_DELAYS_MS[attempt]);
    }

    await requestWalletSync({
      trigger: WalletSyncTrigger.TransactionSubmitted,
      silent: true,
    }).catch(() => undefined);

    const response = await queryClient
      .fetchQuery(getTransactionHashLookupOptions(digest))
      .catch(() => null);
    if (!response?.transactions.some((transaction) => transaction.onchain?.hash === digest)) {
      continue;
    }

    await Promise.all([
      invalidateTransactionViews(queryClient),
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEYS.walletPortfolio],
      }),
      queryClient.invalidateQueries({
        queryKey: [PORTFOLIO_QUERY_KEYS.walletSyncStatus],
      }),
    ]);
    return;
  }
};
